import { withTranslation } from '@application/i18n/with_translation';
import { DialogAlert, Flex, Spacer, Stack, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { CaseCard } from '@component/case_card/case_card.component';
import { Shimmer } from '@component/new_shimmer.component';
import { AppointmentCaseModel } from '@feature/cases/case_appointment_model';
import { MessagingCaseModel } from '@feature/cases/case_messaging_model';
import { CaseModelShared } from '@feature/cases/case_model_shared';
import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { CounterState, useCounterState } from '../../application/counter';
import { EmptyState as EmptyStateLocal } from './_empty_state.component';
import type { CasesCollection } from './view_patient_cases.controller';

interface Props {
	caseCollection: CasesCollection | null;
	value: string;
	selectedCase?: CaseModelShared;
}

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
	`,
	cardLabelContainer: styled.div`
		padding-left: ${tokens.spacing.x2};
		width: 100%;
		margin-bottom: ${tokens.spacing.x1};
	`,
	cardLabelText: styled(Text.h2)`
		font: ${tokens.text.heading.xSmall};

		${tokens.breakpointQuery.medium} {
			font: ${tokens.text.label.largeSemiBold};
		}
	`,
	section: styled.ul`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x1};
		padding: 0;
	`,
};

export function CaseList({ caseCollection, selectedCase, value }: Props): JSX.Element {
	const selectedCaseIdentifier = selectedCase?.getIdentifier();

	if (caseCollection == null) return <Skeleton />;

	return (
		<elements.container>
			<elements.cardLabelContainer>
				<elements.cardLabelText variant='headingSmall'>
					{withTranslation('PW.Component.Visits.CaseList.Title.Appointments')}
				</elements.cardLabelText>
			</elements.cardLabelContainer>

			{caseCollection.appointmentCases.length > 0 ? (
				<elements.section>
					{caseCollection.appointmentCases.map(($case) => {
						const data = $case.get();

						const isSelected = data.ticket === selectedCaseIdentifier;
						const isPhysical = data.type === 'physical';

						return (
							<Fragment key={data.ticket}>
								{data.reservedUntil ? <ReservedUntilDialogAlert reservedUntil={data.reservedUntil} model={$case} /> : null}

								<Flex as='li'>
									<CaseCard
										issuesTrackedBy={AppointmentCaseModel.APPOINTMENT_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + data.ticket}
										to={`./video/${data.ticket}`}
										isSelected={isSelected}
										participants={data.participants}
										reservedUntil={data.reservedUntil ?? undefined}
										showReceptionName={data.receptionName}
										showTime={data.date}
										showBannerText={
											data.lobbyIsOpen && !data.completed && !isPhysical
												? withTranslation('PW.Component.CaseCard.LobbyIsOpen')
												: undefined
										}
										showRfv={data.rfv ?? undefined}
										showRepresentedPatient={data.representative?.name || data.representative?.for}
										showOtherRepresentatives={data.otherRepresentativeNames}
										currentUserSubjectRole={data.currentUserSubjectRole}
										type={data.type}
										showLabel={
											isPhysical
												? withTranslation('PW.Component.CaseCard.Label.Physical')
												: withTranslation('PW.Component.CaseCard.Label.Video')
										}
										shownLabelIcon={data.type === 'video' ? 'video' : 'pinMark'}
									/>
								</Flex>
							</Fragment>
						);
					})}
				</elements.section>
			) : (
				<EmptyStateLocal>
					{value === 'tab1'
						? withTranslation('PW.Component.Visits.CaseList.NoOngoingAppointments')
						: withTranslation('PW.Component.Visits.CaseList.NoCompletedAppointments')}
				</EmptyStateLocal>
			)}

			<Spacer vertical={tokens.spacing.x4} />

			<elements.cardLabelContainer>
				<elements.cardLabelText variant='labelLargeSemiBold'>
					{withTranslation('PW.Component.Visits.CaseList.Title.Messages')}
				</elements.cardLabelText>
			</elements.cardLabelContainer>

			{caseCollection.messagingCases.length > 0 ? (
				<elements.section>
					{caseCollection.messagingCases.map(($case) => {
						const data = $case.get();

						return (
							<Flex key={data.id} as='li'>
								<CaseCard
									issuesTrackedBy={MessagingCaseModel.MESSAGE_UNHANDLED_ISSUES_TRACKING_ID_PREFIX + data.id}
									to={`./message/${data.id}`}
									isSelected={data.id === selectedCaseIdentifier}
									participants={data.participants}
									showReceptionName={data.receptionName}
									type='messaging'
									showTime={data.date}
									showRfv={data.rfv ?? undefined}
									showRepresentedPatient={data.representativeName ?? data.representativeNiN ?? undefined}
									showOtherRepresentatives={data.otherRepresentativeNames}
									currentUserSubjectRole={data.currentUserSubjectRole}
								/>
							</Flex>
						);
					})}
				</elements.section>
			) : (
				<EmptyStateLocal>
					{value === 'tab1'
						? withTranslation('PW.Component.Visits.CaseList.NoOngoingMessageConversations')
						: withTranslation('PW.Component.Visits.CaseList.NoCompletedMessageConversations')}
				</EmptyStateLocal>
			)}
		</elements.container>
	);
}

interface ReservedUntilDialogAlertProps {
	reservedUntil: CounterState;
	model: AppointmentCaseModel;
}

function ReservedUntilDialogAlert({ reservedUntil, model }: ReservedUntilDialogAlertProps): JSX.Element {
	const [isOpen, setIsOpen] = useState(false);
	const { hasElapse, hasBeenTriggeredExternally } = useCounterState(reservedUntil);

	function handleAbort(): void {
		setIsOpen(false);
		const [state, disposer] = reservedUntil;

		state.write({ hasBeenTriggeredExternally: true });
		disposer();

		model.remove();
	}

	useEffect(
		function whenHasElapseChanges() {
			if (hasBeenTriggeredExternally === true) return;

			setIsOpen(hasElapse);
		},
		[hasElapse, hasBeenTriggeredExternally],
	);

	return (
		<DialogAlert
			abortText={withTranslation('Common.Button.Close')}
			isOpen={isOpen}
			header={withTranslation('PW.ReservedUntil.Dialog.Title')}
			description={withTranslation('PW.ReservedUntil.Dialog.Description')}
			onAbort={handleAbort}
		/>
	);
}

function Skeleton() {
	return (
		<Stack gap={tokens.spacing.x2}>
			<Stack gap={tokens.spacing.x1}>
				<Spacer horizontal={tokens.spacing.x2}>
					<Shimmer width={50} height={24} />
				</Spacer>
				<Shimmer width='100%' height={100} cornerRadius={16} opacity={0.5} />
			</Stack>
			<Stack gap={tokens.spacing.x1}>
				<Spacer horizontal={tokens.spacing.x2}>
					<Shimmer width={50} height={24} opacity={0.35} />
				</Spacer>
				<Shimmer width='100%' height={100} cornerRadius={16} opacity={0.2} />
			</Stack>
		</Stack>
	);
}
